import { ReactNode, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, DialogTrigger, Popover } from '@backstage/ui';
import { useAccountsContext } from '@internal/backstage-plugin-catalog-devops';
import { useProjectsContext } from '@internal/backstage-plugin-devops-admin';
import {
  AccountEntity,
  ClusterEntity,
  getAccountSyncState,
} from '@internal/backstage-plugin-catalog-devops-common';
import { ChevronRightIcon, DomainIcon } from './Icons';
import { ClusterTypeChip, ProviderBadge, entityHref, providerOf } from './ProviderBadge';
import styles from './Sidebar.module.css';

/**
 * Collapsed/expanded state of the project groups, persisted across
 * reloads. A group whose account/cluster page is currently open also
 * opens by default unless the user explicitly collapsed it.
 */
const EXPANDED_KEY = '@itransition/sidebar/expandedGroups';

function readExpandedGroups(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function isChildActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

const dotToneClass = (tone: string): string => {
  if (tone === 'success') return styles.dotOk;
  if (tone === 'warning') return styles.dotWarn;
  return '';
};

function SyncDot({ account, name }: { account: AccountEntity; name: string }) {
  const state = getAccountSyncState(account);
  return (
    <span
      className={`${styles.dot} ${dotToneClass(state.tone)}`}
      title={`${name} · ${state.label}`}
    />
  );
}

function ProjectGroupButton(props: {
  label: string;
  open?: boolean;
  controlsId: string;
  onToggle?: () => void;
}) {
  const { label, open, controlsId, onToggle } = props;
  // BUI Button (not a raw <button>) so the RAC DialogTrigger press handlers
  // merge correctly in rail mode; onPress covers the inline expand too.
  return (
    <Button
      type="button"
      variant="tertiary"
      className={styles.navItem}
      aria-expanded={open}
      aria-controls={controlsId}
      aria-label={label}
      onPress={onToggle}
    >
      <DomainIcon />
      <span className={styles.lbl}>{label}</span>
      <span className={styles.end}>
        <ChevronRightIcon size={15} />
      </span>
    </Button>
  );
}

function ProjectGroup(props: {
  title: string;
  name: string;
  href: string;
  open: boolean;
  onToggle: () => void;
  railMode: boolean;
  children: ReactNode;
}) {
  const { title, name, href, open, onToggle, railMode, children } = props;
  const subId = `sb-sub-${name}`;

  const rows = (
    <ProjectGroupButton
      label={title}
      open={railMode ? undefined : open}
      controlsId={subId}
      onToggle={railMode ? undefined : onToggle}
    />
  );

  if (railMode) {
    // In rail mode the group opens as a flyout next to the rail instead of
    // expanding inline.
    return (
      <div>
        <DialogTrigger>
          {rows}
          <Popover placement="right top" hideArrow>
            <FlyoutContent title={title} href={href}>
              {children}
            </FlyoutContent>
          </Popover>
        </DialogTrigger>
      </div>
    );
  }

  return (
    <div className={open ? styles.groupOpen : undefined}>
      {rows}
      <div className={styles.groupSub} id={subId}>
        <div>{children}</div>
      </div>
    </div>
  );
}

function FlyoutContent(props: {
  title: string;
  href: string;
  children: ReactNode;
}) {
  const { title, href, children } = props;
  return (
    <div style={{ minWidth: 224 }}>
      <div className={styles.popTitle}>{`${title} — Accounts`}</div>
      {children}
      <hr className={styles.popDiv} />
      <Link to={href} className={styles.popItem}>
        <DomainIcon size={16} />
        Open project page
      </Link>
    </div>
  );
}

interface ProjectsSectionProps {
  clusters: ClusterEntity[];
  railMode: boolean;
}

/**
 * The PROJECTS section: one collapsible group per Domain ("project") with
 * its accounts (provider badge + sync dot) and a CLUSTERS sub-section.
 */
export function ProjectsSection({ clusters, railMode }: ProjectsSectionProps) {
  const { projects, isLoading } = useProjectsContext();
  const { accounts } = useAccountsContext();
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    readExpandedGroups,
  );

  const toggle = (name: string, next: boolean) => {
    setExpanded(prev => {
      const nextMap = { ...prev, [name]: next };
      try {
        window.localStorage.setItem(EXPANDED_KEY, JSON.stringify(nextMap));
      } catch {
        // private mode — session-only expansion is fine
      }
      return nextMap;
    });
  };

  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) =>
        (a.metadata.title ?? a.metadata.name).localeCompare(
          b.metadata.title ?? b.metadata.name,
        ),
      ),
    [projects],
  );

  if (isLoading) {
    return (
      <div role="group" aria-label="Projects">
        {[0, 1, 2].map(i => (
          <div key={i} className={styles.skeletonRow} />
        ))}
      </div>
    );
  }

  if (sortedProjects.length === 0) {
    return (
      <div role="group" aria-label="Projects">
        <div className={styles.empty}>No projects yet</div>
      </div>
    );
  }

  return (
    <div role="group" aria-label="Projects">
      {sortedProjects.map(project => {
        const name = project.metadata.name;
        const title = project.metadata.title ?? name;
        const href = entityHref(project, 'domain');
        const projectAccounts = accounts
          .filter(account => account.spec?.domain === name)
          .sort((a, b) => a.metadata.name.localeCompare(b.metadata.name));
        const projectClusters = clusters
          .filter(cluster => cluster.spec?.domain === name)
          .sort((a, b) => a.metadata.name.localeCompare(b.metadata.name));

        const hasActiveChild =
          projectAccounts.some(account =>
            isChildActive(pathname, entityHref(account, 'account')),
          ) ||
          projectClusters.some(cluster =>
            isChildActive(pathname, entityHref(cluster, 'cluster')),
          );
        const open = expanded[name] ?? hasActiveChild;

        return (
          <ProjectGroup
            key={name}
            name={name}
            title={title}
            href={href}
            open={open}
            onToggle={() => toggle(name, !open)}
            railMode={railMode}
          >
            {projectAccounts.map(account => {
              const accountHref = entityHref(account, 'account');
              const active = isChildActive(pathname, accountHref);
              return (
                <Link
                  key={account.metadata.name}
                  to={accountHref}
                  className={styles.navItem}
                  aria-current={active ? 'page' : undefined}
                >
                  <ProviderBadge provider={providerOf(account.spec?.type)} />
                  <SyncDot account={account} name={account.metadata.name} />
                  <span className={styles.lbl}>{account.metadata.name}</span>
                </Link>
              );
            })}
            {projectClusters.length > 0 && (
              <div className={styles.subsec}>Clusters</div>
            )}
            {projectClusters.map(cluster => {
              const clusterHref = entityHref(cluster, 'cluster');
              const active = isChildActive(pathname, clusterHref);
              return (
                <Link
                  key={cluster.metadata.name}
                  to={clusterHref}
                  className={styles.navItem}
                  aria-current={active ? 'page' : undefined}
                >
                  <ClusterTypeChip type={cluster.spec?.type} />
                  <span className={styles.lbl}>{cluster.metadata.name}</span>
                </Link>
              );
            })}
          </ProjectGroup>
        );
      })}
    </div>
  );
}
